import { renderChunkFallback } from "./lib/chunkReloadFallback";
/**
 * Main entry point - v2 build reset
 */
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Initialize i18next
import "./lib/i18n";

// Initialize GA4 (no-op until GA4_MEASUREMENT_ID is configured)
import { initAnalytics } from "./lib/analytics";
import { initAutoEvents } from "./lib/analytics-auto-events";
initAnalytics();
initAutoEvents();

// Apply homeContent i18n patches (12 + 5 additional languages)
import { applyHomeContentPatch } from "./data/homeContent-i18n-patch";
import { applyHomeContentTier2Patch } from "./data/homeContent-tier2-patch";
applyHomeContentPatch();
applyHomeContentTier2Patch();

// Translated SEO catalog is a separate chunk: load it before the first render
// on non-English URLs (same metadata as before), prefetch on idle otherwise.
import { ensureSeoI18n, pathNeedsSeoI18n } from "./lib/seo-i18n-loader";
import { captureUtmParams } from "./lib/utm";

// SEO-1910b: persist UTM from the landing URL for the whole session, so a lead
// sent later from /start or any form keeps its acquisition source.
captureUtmParams();

const root = document.getElementById("root");
const renderApp = () => { if (root) createRoot(root).render(<App />); };
if (pathNeedsSeoI18n(window.location.pathname)) {
  ensureSeoI18n().then(renderApp, (err) => {
    console.error('[seo-i18n] translated catalog failed to load', err);
    renderApp();
  });
} else {
  renderApp();
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
  const prefetch = () => { ensureSeoI18n().catch((err) => console.warn('[seo-i18n] prefetch failed', err)); };
  if (idle) idle(prefetch); else setTimeout(prefetch, 3000);
}

declare global {
  interface Window {
    __ubtClearModuleReloadFlag?: () => void;
  }
}

// FF-1910: clear the reload guard only once the page has stayed up past the
// 30 s window — clearing it at boot let a failing page reload forever.
setTimeout(() => window.__ubtClearModuleReloadFlag?.(), 31000);

// Auto-recover from stale dynamic-import chunks after a new deploy.
// When the browser holds old HTML or an old route chunk hash, reload with a
// cache-busting URL once every 30s so the fresh HTML + chunk manifest is used.
const CHUNK_RELOAD_KEY = '__ubt_module_reload_at__';
const isChunkLoadError = (msg: string) =>
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|ChunkLoadError/i.test(msg);

const tryReloadOnce = () => {
  try {
    const now = Date.now();
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0');
    if (now - lastReload < 30000) {
      // Already reloaded recently: never leave a blank screen, offer a manual reload.
      const root = document.getElementById('root');
      if (root && !root.textContent?.trim()) {
        renderChunkFallback(root, window.location.pathname);
      }
      return;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('__ubt_reload', String(now));
    window.location.replace(nextUrl.toString());
  } catch {
    window.location.reload();
  }
};

// IMPORTANT: Only reload on real dynamic-import chunk errors (they carry a
// descriptive message). Do NOT reload on generic resource (script/link)
// failures — optional <link rel="prerender">/preload/prefetch tags and
// third-party scripts (analytics, chat widgets) can fail intermittently on
// preview/staging, and reacting to them caused a 30-second reload loop on
// the live preview.
// "ResizeObserver loop ..." is a benign browser notification (layout settled
// in the next frame), not a real failure. Swallow it so error overlays and
// monitoring don't treat it as a crash.
window.addEventListener('error', (e: ErrorEvent) => {
  if (typeof e?.message === 'string' && e.message.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);

window.addEventListener('error', (e: ErrorEvent) => {
  const msg = e?.message || '';
  if (isChunkLoadError(msg)) tryReloadOnce();
});
window.addEventListener('unhandledrejection', (e) => {
  const msg = e?.reason?.message || String(e?.reason || '');
  if (isChunkLoadError(msg)) tryReloadOnce();
});
// React.lazy failures are caught by error boundaries (no unhandledrejection), so the
// screen stayed blank. Vite emits this event for every failed dynamic import.
window.addEventListener('vite:preloadError', () => {
  tryReloadOnce();
});


// Register Service Worker on the canonical production domain only.
// This avoids preview/staging cache pollution and keeps SEO rollouts fresh.
const CANONICAL_SW_HOSTS = new Set(['bpo-contact.com', 'www.bpo-contact.com']);

if ('serviceWorker' in navigator && import.meta.env.PROD && CANONICAL_SW_HOSTS.has(window.location.hostname.toLowerCase())) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // SW registration failed — non-critical, app works without it
    });
  });
}
